<template>
  <div>
    <LayoutContent>
      <SectionTitle :label="$t('label.transferToBank')" />
      <v-card class="rounded-lg overflow-hidden elevation-3">
        <div v-if="currentStep === 0">
          <section class="mt-10 px-8">
            <SectionTitle :label="$t('label.addedBankAccount')" class="ma-0" />
          </section>
          <v-divider class="mb-10 grey lighten-2" />
          <section class="mb-10" style="min-height: 300px" :class="$vuetify.breakpoint.smAndUp ? 'px-8' : 'px-4'">
            <div v-if="bankList.length > 0">
              <v-radio-group v-model="form.bank_id">
                <v-row>
                  <v-col :sm="12" :md="6" :cols="12" v-for="bank in bankList" :key="bank.bank_id">
                    <v-card color="white">
                      <div class="pa-4 d-flex flex-row justify-space-between align-center">
                        <div class="d-flex flex-row align-center">
                          <v-radio :value="bank.bank_id" style="background-color: #fff" @click="selectBank(bank)"></v-radio>
                          <div class="mx-4 rounded" style="border: 1px solid #efefef">
                            <div class="white w-full d-flex rounded" style="width: 80px; height: 80px">
                              <v-img :src="bank.bank_image" style="object-fit: fit" class="ma-auto w-full"></v-img>
                            </div>
                          </div>
                          <div>
                            <p class="text-subtitle-1 font-weight-bold">{{ bank.bank_name }}</p>
                            <p class="text-subtitle-2 font-weight-regular" style="line-height: 120%">{{ bank.account_holder_name }}</p>
                            <p class="text-subtitle-2 font-weight-regular" style="line-height: 120%">{{ bank.account_no }}</p>
                          </div>
                        </div>
                        <v-btn class="mx-2" x-small fab color="primary" outlined icon @click="deleteBank(bank)">
                          <v-icon color="primary">mdi-close</v-icon>
                        </v-btn>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-radio-group>
            </div>
            <div v-else class="d-flex" style="height: 250px">
              <p class="ma-auto text-h6 font-weight-medium">{{ $t('message.noAccountInRecord') }}</p>
            </div>
          </section>
        </div>
        <v-form ref="transferForm" v-else>
          <div class="coin-conversion--container" v-if="currentAgent">
            <div class="d-flex flex-column justify-center pa-8 primary lighten-1 white--text">
              <h4 class="text-center text-h5 font-weight-bold">{{ $t('label.availableCashBalance') }}</h4>
              <p class="text-center text-h6 font-weight-medium">{{ currentAgent.currency }} {{ currentAgent.total_cashback }}</p>
            </div>
            <div class="pa-8">
              <p class="font-weight-bold text-subtitle-1">{{ $t('label.transferAmount') }}</p>
              <v-text-field
                v-model="form.amount"
                placeholder="0"
                color="primary lighten-1"
                class="d-block mb-auto shrink primary--input text-h5"
                :rules="$rules.requiredRules"
                :prefix="currentAgent.currency"
                hide-details="auto"
                single-line
              />
              <div class="text-subtitle-2 text-right">{{ $t('label.minimumTransferAmt', { currency: currentAgent.currency, amount: agent_min_withdrawal_amount.toFixed(2) }) }}</div>
            </div>
            <div class="coin-options--wrapper pa-8">
              <v-btn
                v-for="amount in presetCashAmounts"
                text
                outlined
                color="primary darken-1"
                class="coin-option font-weight-bold"
                :key="amount"
                @click="form.amount = amount"
                :disabled="amount > currentAgent.total_cashback"
              >
                {{ amount }}
              </v-btn>
            </div>
          </div>
        </v-form>
      </v-card>
    </LayoutContent>
    <bottom-bar>
      <v-container class="py-3 d-flex align-center justify-center flex-wrap">
        <app-button
          v-if="currentStep === 0"
          :label="$t('button.oneTimeTransfer')"
          class="mr-2"
          :class="!$vuetify.breakpoint.smAndUp && 'mb-2'"
          :width="$vuetify.breakpoint.smAndUp ? '12rem' : '10rem'"
          :to="{ name: $constants.ROUTE_NAME.AGENT_ONE_TIME_TRANSFER }"
        />
        <app-button
          v-if="currentStep === 0"
          :label="$t('button.addBankAccount')"
          class="mr-2"
          :class="!$vuetify.breakpoint.smAndUp && 'mb-2'"
          :width="$vuetify.breakpoint.smAndUp ? '12rem' : '10rem'"
          :to="{ name: $constants.ROUTE_NAME.AGENT_ADD_BANK_ACCOUNT }"
        />
        <app-button
          v-if="currentStep === 0"
          :label="$t('button.next')"
          class="mr-2"
          :width="$vuetify.breakpoint.smAndUp ? '12rem' : '10rem'"
          @click="currentStep = currentStep + 1"
          :disabled="this.form.bank_id ? false : true"
        />
        <app-button v-if="currentStep === 1" :label="$t('label.convert')" class="mr-2" :width="$vuetify.breakpoint.smAndUp ? '12rem' : '10rem'" @click="transfer" />
      </v-container>
    </bottom-bar>
  </div>
</template>

<script>
import SectionTitle from '@/components/SectionTitle.vue'
import LayoutContent from '@/views/layouts/LayoutContent.vue'
import { mapState, mapActions, mapGetters } from 'vuex'
import { localeHelper } from '@/utils'

export default {
  name: 'TransferToBank',
  components: { LayoutContent, SectionTitle },
  data: () => ({
    presetCashAmounts: [50, 100, 150, 200],
    bankList: [],
    currentStep: 0,
    form: {
      bank_id: null,
      account_type: null,
      account_no: null,
      account_holder_name: '',
      email: '',
      amount: 0
    }
  }),
  computed: {
    ...mapGetters('page', ['agent_min_withdrawal_amount']),
    ...mapState('agent', ['currentAgent'])
  },
  created() {
    this.getBankList()
  },
  methods: {
    ...mapActions('agent', ['getUserWithdrawalBanks', 'deleteUserWithdrawalBank', 'transferToBank']),
    getBankList() {
      this.getUserWithdrawalBanks().then((res) => {
        this.bankList = res
      })
    },
    selectBank(bank) {
      this.form = {
        bank_id: bank.bank_id,
        account_type: bank.account_type,
        account_no: bank.account_no,
        account_holder_name: bank.account_holder_name,
        email: bank.email,
        amount: 0
      }
    },
    deleteBank(bank) {
      this.deleteUserWithdrawalBank(bank.id).then((res) => {
        this.$toast.success(res.message)
        this.getBankList()
      })
    },
    transfer() {
      if (!this.$refs.transferForm.validate()) {
        return true
      }
      this.transferToBank(this.form)
        .then((res) => {
          this.$router.push({ name: this.$constants.ROUTE_NAME.AGENT_TRANSFER_TO_BANK_COMPLETE })
        })
        .catch((err) => this.$toast.error(err.error_message))
    }
  }
}
</script>

<style lang="scss" scoped>
.coin-conversion--container {
  display: grid;
  grid-template-columns: 1fr;
  @media only screen and (min-width: 1000px) {
    grid-template-columns: 2fr 3fr 3fr;
  }
}

.coin-options--wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.coin-option {
  border-color: var(--v-primary-lighten1) !important;
}

.v-radio .theme--light {
  background-color: #fff !important;
}

::v-deep .v-input--selection-controls__input > i {
  background-color: #fff !important;
}

::v-deep .v-btn {
  background-color: #fff !important;
}

::v-deep i {
  background-color: inherit !important;
}

::v-deep .v-input input {
  max-height: auto;
  padding: 0;
}
</style>
